import { emptySplitApi as api } from "./emptyApi"
import { CompanyDto, UserDto } from "./types"

const injectedRtkApi = api.injectEndpoints({
  endpoints: build => ({
    companyControllerSignup: build.mutation<
      CompanyControllerSignupApiResponse,
      CompanyControllerSignupApiArg
    >({
      query: queryArg => ({
        url: `/company/signup`,
        method: "POST",
        body: queryArg.signupDto,
      }),
    }),
    companyControllerSignUpCompany: build.mutation<
      CompanyControllerSignUpCompanyApiResponse,
      CompanyControllerSignUpCompanyApiArg
    >({
      query: queryArg => ({
        url: "/company/sign-up",
        method: "POST",
        body: queryArg.signUpCompanyDto,
      }),
    }),
    companyControllerCreate: build.mutation<
      CompanyControllerCreateApiResponse,
      CompanyControllerCreateApiArg
    >({
      query: queryArg => ({
        url: `/company`,
        method: "POST",
        body: queryArg.createCompanyDto,
      }),
    }),
    companyControllerFindAllCompanies: build.query<
      CompanyControllerFindAllCompaniesApiResponse,
      CompanyControllerFindAllCompaniesApiArg
    >({
      query: () => ({ url: `/company` }),
    }),
    companyControllerGetCompanyById: build.query<
      CompanyControllerGetCompanyByIdApiResponse,
      CompanyControllerGetCompanyByIdApiArg
    >({
      query: queryArg => ({ url: `/company/${queryArg.id}` }),
    }),
    companyControllerGetIntegrationSetupStatus: build.query<
      CompanyControllerGetIntegrationSetupStatusApiResponse,
      CompanyControllerGetIntegrationSetupStatusApiArg
    >({
      query: queryArg => ({ url: `/company/${queryArg.id}/setup-status` }),
    }),
    companyControllerUpdateCompany: build.mutation<
      CompanyControllerUpdateCompanyApiResponse,
      CompanyControllerUpdateCompanyApiArg
    >({
      query: queryArg => ({
        url: `/company/${queryArg.id}`,
        method: "PUT",
        body: queryArg.updateCompanyDto,
      }),
    }),
    companyControllerRemoveCompany: build.mutation<
      CompanyControllerRemoveCompanyApiResponse,
      CompanyControllerRemoveCompanyApiArg
    >({
      query: queryArg => ({ url: `/company/${queryArg.id}`, method: "DELETE" }),
    }),
    companyControllerUpgradeToStandard: build.mutation<
      CompanyControllerUpgradeToStandardApiResponse,
      CompanyControllerUpgradeToStandardApiArg
    >({
      query: queryArg => ({
        url: `/company/${queryArg.id}/upgrade-to-standard`,
        method: "POST",
      }),
    }),
    companyControllerGetFinancials: build.query<
      CompanyControllerGetFinancialsApiResponse,
      CompanyControllerGetFinancialsApiArg
    >({
      query: queryArg => ({ url: `/company/${queryArg.id}/financials` }),
      providesTags: ["CompanyFinancials"],
    }),
    companyControllerUpsertFinancials: build.mutation<
      CompanyControllerUpsertFinancialsApiResponse,
      CompanyControllerUpsertFinancialsApiArg
    >({
      query: queryArg => ({
        url: `/company/${queryArg.id}/financials`,
        method: "PUT",
        body: queryArg.body,
      }),
      invalidatesTags: ["CompanyFinancials"],
    }),
  }),
  overrideExisting: false,
})
export { injectedRtkApi as generatedApi }
export type CompanyControllerSignupApiResponse = /** status 200  */ SignupDto
export type CompanyControllerSignupApiArg = {
  signupDto: SignupDto
}

export type CompanyControllerSignUpCompanyApiResponse = {
  user: UserDto
  company: CompanyDto
}
export type CompanyControllerSignUpCompanyApiArg = {
  signUpCompanyDto: SignUpCompanyDto
}

export type CompanyControllerCreateApiResponse = /** status 200  */ CompanyDto
export type CompanyControllerCreateApiArg = {
  createCompanyDto: CreateCompanyDto
}
export type CompanyControllerFindAllCompaniesApiResponse =
  /** status 200  */ CompanyDto[]
export type CompanyControllerFindAllCompaniesApiArg = void
export type CompanyControllerGetCompanyByIdApiResponse =
  /** status 200  */ CompanyDto
export type CompanyControllerGetCompanyByIdApiArg = {
  id: string
}
export type CompanyControllerUpdateCompanyApiResponse =
  /** status 200  */ CompanyDto
export type CompanyControllerUpdateCompanyApiArg = {
  id: string
  updateCompanyDto: UpdateCompanyDto
}
export type CompanyControllerRemoveCompanyApiResponse =
  /** status 200  */ CompanyDto
export type CompanyControllerRemoveCompanyApiArg = {
  id: string
}
export type CompanyControllerUpgradeToStandardApiResponse =
  /** status 200  */ CompanyDto
export type CompanyControllerUpgradeToStandardApiArg = {
  id: string
}
export type CompanyControllerGetIntegrationSetupStatusApiResponse =
  /** status 200  */ IntegrationSetupStatusDto
export type CompanyControllerGetIntegrationSetupStatusApiArg = {
  id: string
}
export type IntegrationSetupStatusDto = {
  integrationsReady: boolean
  fivetranReady: boolean
  airbyteReady: boolean
  stripeReady: boolean
  message?: string
}
export type SignupDto = {
  companyName: string
  name: string
  email: string
  pid: string
}

export type SignUpCompanyDto = {
  domain: string
  companyName: string
  metadata?: {
    setup: {
      revenueEstimate: {
        from: number
        to: number
      }
    }
  }
}

export type CreateCompanyDto = {
  name: string
}
export type UpdateCompanyDto = Partial<
  Pick<
    CompanyDto,
    "name" | "metadata" | "setupStatus" | "domain" | "isAgencyClient"
  >
>
export type CompanyFinancialsDto = {
  companyId: string
  maxSpendPercentRev?: number
  minSpendPercentRev?: number
  targetBrandSpendPercentOfTotal?: number
  blendedGrossMargin?: number
  spendIntoMarginalLtv?: "first_order" | "90d" | "180d" | "365d"
  shippingCostPercentAssm?: number
  ccFeePercentAssm?: number
  targetDRRoas?: number
  targetBrandRoas?: number
  targetDailyBudget?: number
  avgOrderValue?: number
  targetDRRoasLocked?: boolean
  targetBrandRoasLocked?: boolean
  targetDailyBudgetLocked?: boolean
  targetMerType?: "amer" | "mer"
  targetMerValue?: number
  targetMerScope?: "ecomm" | "total"
  targetMerDisplay?: "amer" | "mer" | "cac"
  dailyBudgetEvalPeriodDays?: number
  manualChangeHoldDays?: number
  /** Budget Recommender: 'fixed' holds BRANDED_SEARCH rows like a pin; 'optimize' reallocates them. */
  brandSearchPolicy?: "fixed" | "optimize"
  /** Budget Recommender step cap, fraction of the current budget (0.10–0.30); null = off. */
  maxBudgetStepPct?: number | null
  amazonDiscountPercent?: number
  amazonRefundRate?: number
  amazonShippingCostPercent?: number
  amazonCogsPercent?: number
  amazonCcFeePercent?: number
  amazonAdSpendPercent?: number
  dtcRetailStaffingCostPercent?: number
  dtcRetailCcFeePercent?: number
  dtcRetailLocalMarketingPercent?: number
  dtcRetailOtherVariableCostPercent?: number
  defaultReturnRate?: number
  returnRateMode?: "computed" | "default" | "max"
  productCostMarkupPercent?: number
  weekStartDay?:
    | "monday"
    | "tuesday"
    | "wednesday"
    | "thursday"
    | "friday"
    | "saturday"
    | "sunday"
}
export type UpsertCompanyFinancialsDto = Omit<CompanyFinancialsDto, "companyId">
export type CompanyControllerGetFinancialsApiResponse =
  CompanyFinancialsDto | null
export type CompanyControllerGetFinancialsApiArg = {
  id: string
}
export type CompanyControllerUpsertFinancialsApiResponse = CompanyFinancialsDto
export type CompanyControllerUpsertFinancialsApiArg = {
  id: string
  body: UpsertCompanyFinancialsDto
}

export const {
  useCompanyControllerSignUpCompanyMutation,
  useCompanyControllerSignupMutation,
  useCompanyControllerCreateMutation,
  useCompanyControllerFindAllCompaniesQuery,
  useCompanyControllerGetCompanyByIdQuery,
  useCompanyControllerUpdateCompanyMutation,
  useCompanyControllerUpgradeToStandardMutation,
  useCompanyControllerRemoveCompanyMutation,
  useLazyCompanyControllerGetCompanyByIdQuery,
  useCompanyControllerGetIntegrationSetupStatusQuery,
  useCompanyControllerGetFinancialsQuery,
  useCompanyControllerUpsertFinancialsMutation,
} = injectedRtkApi
